import { createFileRoute } from '@tanstack/react-router';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Spinner } from '@/components/admin/ui';
import {
  EmptyState,
} from '@/components';
import type {
  AcquisitionBackfillStatus,
  AcquisitionFunnelDay,
  AcquisitionFunnelsResponse,
} from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

const PAGE_SIZE = 7;
const SCROLL_FETCH_MIN_MS = 1500;
const POLL_INTERVAL_MS = 15000;

function formatDayLabel(iso: string): string {
  const [y, m, d] = iso.split('-');
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatStepRate(count: number, prior: number): string {
  if (prior <= 0) return '—';
  const pct = (count / prior) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

function formatOverallRate(count: number, top: number): string {
  if (top <= 0) return '0%';
  const pct = (count / top) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

function AcquisitionFunnelRow({ day }: { day: AcquisitionFunnelDay }) {
  const topCount = day.steps[0]?.count ?? 0;
  const lastStep = day.steps[day.steps.length - 1];
  const isPending = day.pending === true;

  return (
    <Card
      className={`overflow-x-auto p-4 ${isPending ? 'border-dashed border-nova-input bg-nova-muted/80' : ''}`}
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-nova-foreground">{formatDayLabel(day.date)}</h2>
          {isPending ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
              Loading…
            </span>
          ) : null}
        </div>
        <p className="text-xs text-nova-muted-foreground">
          Overall conversion{' '}
          <span className="font-medium tabular-nums text-nova-foreground">
            {formatOverallRate(lastStep?.count ?? 0, topCount)}
          </span>
        </p>
      </div>
      <div className="flex min-w-max items-stretch gap-1">
        {day.steps.map((step, idx) => {
          const prevCount = idx === 0 ? step.count : (day.steps[idx - 1]?.count ?? 0);
          const widthPct =
            topCount > 0 ? Math.max(8, Math.min(100, (step.count / topCount) * 100)) : 8;
          return (
            <div key={step.key} className="flex items-stretch">
              {idx > 0 ? (
                <div className="flex w-10 shrink-0 flex-col items-center justify-center px-0.5">
                  <span className="text-[10px] font-medium tabular-nums text-nova-muted-foreground">→</span>
                  <span className="text-[10px] tabular-nums text-nova-muted-foreground">
                    {formatStepRate(step.count, prevCount)}
                  </span>
                </div>
              ) : null}
              <div
                className="flex w-28 shrink-0 flex-col justify-between rounded-lg border border-nova-border bg-nova-card p-2 shadow-sm"
                style={{
                  borderTopWidth: 3,
                  borderTopColor: PRIMARY_HEX,
                  minHeight: 72,
                  opacity: isPending ? 0.75 : 1,
                }}
              >
                <div>
                  <p className="text-[11px] font-medium leading-tight text-nova-foreground">{step.label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-nova-foreground">
                    {formatInt(step.count)}
                  </p>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-nova-muted">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${widthPct}%`, backgroundColor: PRIMARY_HEX }}
                  />
                </div>
                <p className="mt-1 text-[10px] tabular-nums text-nova-muted-foreground">
                  {formatOverallRate(step.count, topCount)} of landing
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BackfillProgress({
  backfill,
  onBackfillHistory,
  isBackfilling,
}: {
  backfill: AcquisitionBackfillStatus | undefined;
  onBackfillHistory: () => void;
  isBackfilling: boolean;
}) {
  if (!backfill) {
    return null;
  }

  const pct =
    backfill.total > 0 ? Math.min(100, Math.round((backfill.complete / backfill.total) * 100)) : 0;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-nova-foreground">Historical backfill</p>
          <p className="mt-0.5 text-xs text-nova-muted-foreground">
            {formatInt(backfill.complete)} / {formatInt(backfill.total)} days cached
            {backfill.pending > 0 ? ` · ${formatInt(backfill.pending)} pending` : ''}
          </p>
        </div>
        <button
          type="button"
          className="rounded-lg border border-nova-input bg-nova-card px-3 py-1.5 text-sm font-medium text-nova-foreground hover:bg-nova-accent disabled:opacity-50"
          disabled={isBackfilling}
          onClick={onBackfillHistory}
        >
          {isBackfilling ? 'Enqueuing…' : 'Backfill history'}
        </button>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-nova-muted">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: PRIMARY_HEX }}
        />
      </div>
      {backfill.rate_limited_until ? (
        <p className="mt-2 text-xs text-amber-700">
          Amplitude rate limited until{' '}
          {new Date(backfill.rate_limited_until).toLocaleString(undefined, {
            timeZone: 'America/Los_Angeles',
          })}{' '}
          PT
        </p>
      ) : null}
    </Card>
  );
}

function AcquisitionPage() {
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const lastFetchAtRef = useRef(0);
  const queryClient = useQueryClient();
  const [scrollBlocked, setScrollBlocked] = useState(false);

  const funnelsQ = useInfiniteQuery({
    queryKey: ['admin', 'acquisition', 'funnels'],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) {
        qs.set('before', pageParam);
      }
      return apiJson<AcquisitionFunnelsResponse>(
        `/v1/admin/analytics/acquisition/funnels?${qs.toString()}`
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.has_more ? (last.next_before ?? undefined) : undefined),
  });

  const days = useMemo(
    () => funnelsQ.data?.pages.flatMap((page) => page.days) ?? [],
    [funnelsQ.data?.pages]
  );

  const backfill = funnelsQ.data?.pages[0]?.backfill;
  const stepHeaders = funnelsQ.data?.pages[0]?.step_labels ?? [];
  const amplitudeError = funnelsQ.data?.pages[0]?.amplitude_error;
  const amplitudeStaleAt = funnelsQ.data?.pages[0]?.amplitude_stale_at;

  const hasPendingRows = days.some((day) => day.pending);
  const shouldPoll = hasPendingRows || (backfill?.pending ?? 0) > 0;

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'acquisition', 'funnels'] });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [shouldPoll, queryClient]);

  const throttledFetchNextPage = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchAtRef.current < SCROLL_FETCH_MIN_MS) {
      setScrollBlocked(true);
      window.setTimeout(() => setScrollBlocked(false), SCROLL_FETCH_MIN_MS);
      return;
    }
    lastFetchAtRef.current = now;
    void funnelsQ.fetchNextPage();
  }, [funnelsQ.fetchNextPage]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !funnelsQ.hasNextPage || funnelsQ.isFetchingNextPage || scrollBlocked) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          throttledFetchNextPage();
        }
      },
      { rootMargin: '240px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    funnelsQ.hasNextPage,
    funnelsQ.isFetchingNextPage,
    throttledFetchNextPage,
    scrollBlocked,
  ]);

  const backfillMutation = useMutation({
    mutationFn: () =>
      apiJson<AcquisitionBackfillStatus>(
        '/v1/admin/analytics/acquisition/backfill?full_history=true',
        { method: 'POST' }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'acquisition', 'funnels'] });
    },
  });

  const isInitialLoading = funnelsQ.isLoading;
  const error = funnelsQ.error;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Acquisition</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Daily mobile funnels from Landing Screen through Subscription Paid (unique Amplitude
          users, Pacific calendar days, 30-day conversion window).
        </p>
      </div>

      <BackfillProgress
        backfill={backfill}
        onBackfillHistory={() => backfillMutation.mutate()}
        isBackfilling={backfillMutation.isPending}
      />

      {amplitudeError ? (
        <Card className="border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">{amplitudeError}</p>
          {amplitudeStaleAt ? (
            <p className="mt-1 text-xs text-amber-800">
              Showing stale cache from{' '}
              {new Date(amplitudeStaleAt).toLocaleString(undefined, {
                timeZone: 'America/Los_Angeles',
              })}{' '}
              PT
            </p>
          ) : null}
        </Card>
      ) : null}

      {stepHeaders.length > 0 ? (
        <div className="hidden overflow-x-auto rounded-lg border border-nova-border bg-nova-card py-2 px-2.5 lg:block">
          <div className="flex min-w-max items-center gap-1 text-xs font-medium text-nova-muted-foreground">
            <span className="w-36 shrink-0">Day</span>
            {stepHeaders.map((step, idx) => (
              <div key={step.key} className="flex items-center">
                {idx > 0 ? <span className="w-10 text-center">→</span> : null}
                <span className="inline-block w-28 text-center">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {isInitialLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load acquisition funnels'}
          </p>
          <button
            type="button"
            className="mt-3 text-sm font-medium text-nova-primary hover:underline"
            onClick={() => void funnelsQ.refetch()}
          >
            Retry
          </button>
        </Card>
      ) : days.length === 0 ? (
        <EmptyState title="No acquisition funnel data" />
      ) : (
        <div className="space-y-4">
          {days.map((day) => (
            <AcquisitionFunnelRow key={day.date} day={day} />
          ))}
        </div>
      )}

      <div ref={loadMoreRef} className="flex justify-center py-6">
        {funnelsQ.isFetchingNextPage ? (
          <Spinner />
        ) : funnelsQ.hasNextPage ? (
          <p className="text-xs text-nova-muted-foreground">Scroll for older days…</p>
        ) : days.length > 0 ? (
          <p className="text-xs text-nova-muted-foreground">End of history</p>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/acquisition')({
  component: AcquisitionPage,
});
